import { Action, createReducer, on } from '@ngrx/store';
import * as NotificationActions from '../actions/notification.actions';
import { environment } from '../../../environments/environment';

export interface NotificationState {
  notifications: NotificationActions.Notification[];
}

const initialState: NotificationState = {
  notifications: [],
};

const _notificationReducer = createReducer(
  initialState,
  on(NotificationActions.ShowNotification, (state, { notification }): NotificationState => {
    // Check if notification already exists by ID (prevent exact duplicates)
    const existsById = state.notifications.some((n) => n.id === notification.id);
    if (existsById) {
      return state;
    }

    // Check for duplicate content within recent timeframe (prevent retry duplicates)
    // Consider it a duplicate if same title, summary, severity within last 2 seconds
    const now = notification.timestamp.getTime();
    const duplicateThresholdMs = 2000; // 2 seconds
    const isDuplicate = state.notifications.some((n) => {
      const timeDiff = now - n.timestamp.getTime();
      const isRecent = timeDiff >= 0 && timeDiff <= duplicateThresholdMs;
      const isSameContent =
        n.title === notification.title &&
        n.summary === notification.summary &&
        n.severity === notification.severity;
      return isRecent && isSameContent;
    });

    if (isDuplicate) {
      return state;
    }

    // Add new notification to the front of the array
    const updatedNotifications = [notification, ...state.notifications];

    // Enforce max notifications limit by removing oldest
    const maxNotifications = environment.notification.maxNotifications;
    const limitedNotifications =
      updatedNotifications.length > maxNotifications
        ? updatedNotifications.slice(0, maxNotifications)
        : updatedNotifications;

    return {
      ...state,
      notifications: limitedNotifications,
    };
  }),
  on(
    NotificationActions.DismissNotification,
    (state, { id }): NotificationState => ({
      ...state,
      notifications: state.notifications.filter((n) => n.id !== id),
    }),
  ),
  on(
    NotificationActions.ClearAllNotifications,
    (state): NotificationState => ({
      ...state,
      notifications: [],
    }),
  ),
  on(
    NotificationActions.NotificationActionClicked,
    (state, { notificationId }): NotificationState => ({
      ...state,
      notifications: state.notifications.filter((n) => n.id !== notificationId),
    }),
  ),
);

export const notificationReducer = (
  state: NotificationState = initialState,
  action: Action,
): NotificationState => _notificationReducer(state, action);
